(() => {
  'use strict';
  const instances = new Map();
  const source = window.HomewoodsExternalReviewData;
  if (!source || !Array.isArray(source.reviews)) return;
  const dateFormat = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const safeUrl = (value) => {
    try {
      const url = new URL(value, location.href);
      if (url.protocol === 'https:' || (url.origin === location.origin && /^\/research\/andor-willow-reviews-2026-10-18\//.test(url.pathname))) return url.href;
    } catch (_) { /* Invalid source URLs are omitted. */ }
    return '';
  };
  const link = (text, href, className) => {
    const anchor = node('a', className, text);
    anchor.href = safeUrl(href) || safeUrl(source.source);
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    return anchor;
  };
  const timestamp = (review) => Date.parse(review.date.replace(' UTC', 'Z').replace(' ', 'T')) || 0;
  const stars = (rating) => {
    const bounded = Math.max(0, Math.min(5, Number(rating) || 0));
    const element = node('span', 'hw-external-reviews__stars', '★'.repeat(bounded) + '☆'.repeat(5 - bounded));
    element.setAttribute('role', 'img');
    element.setAttribute('aria-label', HWT`${bounded} out of 5 stars`);
    return element;
  };
  const sourceImage = (url, alt, asset) => {
    const image = node('img');
    image.src = safeUrl(window.HomewoodsAssetUrls?.[asset?.localPath] || url);
    image.alt = String(alt || '').replace(/Andor Willow/gi, 'the source brand');
    image.loading = 'lazy';
    image.decoding = 'async';
    if (asset?.width && asset?.height) {
      image.width = asset.width;
      image.height = asset.height;
    }
    return image;
  };
  const createCard = (review) => {
    const card = node('article', 'hw-external-review');
    card.dataset.sourceReviewId = review.id;
    const top = node('div', 'hw-external-review__top');
    const date = node('time', '', timestamp(review) ? dateFormat.format(timestamp(review)) : HWT("Date unavailable"));
    if (timestamp(review)) date.dateTime = new Date(timestamp(review)).toISOString();
    top.append(stars(review.rating), date);
    card.append(top, node('h3', 'hw-external-review__author', review.author || HWT("Customer")));
    if (review.location) card.append(node('p', 'hw-external-review__location', review.location));
    card.append(node('p', 'hw-external-review__product', `Project · ${review.product || HWT("Source product")}`));
    const photos = node('div', 'hw-external-review__photos');
    (review.photos || []).filter(safeUrl).forEach((url, index) => {
      const photo = link('', url, 'hw-external-review__photo');
      photo.setAttribute('aria-label', HWT`Open ${review.author}'s original review photo ${index + 1}`);
      photo.append(sourceImage(url, `${review.author}'s ${review.product || 'panel'} installation, shared in the source collection`, source.photoAssets?.[url]));
      photos.append(photo);
    });
    if (photos.children.length) card.append(photos);
    return card;
  };
  const mount = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const cards = root.querySelector('[data-external-review-cards]');
    const status = root.querySelector('[data-external-review-status]');
    const more = root.querySelector('[data-external-review-more]');
    const empty = root.querySelector('[data-external-review-empty]');
    const photoOnly = root.querySelector('[data-external-review-photos]');
    const sort = root.querySelector('[data-external-review-sort]');
    const reset = root.querySelector('[data-external-review-reset]');
    const ratingButtons = [...root.querySelectorAll('[data-external-review-rating]')];
    const initialLimit = 18;
    const pageSize = 6;
    let rating = 0;
    let limit = initialLimit;
    const render = (append = false) => {
      const filtered = source.reviews.filter((review) => (!rating || review.rating === rating) && (!photoOnly.checked || review.photos?.length));
      filtered.sort((a, b) => {
        if (sort.value === 'highest') return b.rating - a.rating || timestamp(b) - timestamp(a);
        if (sort.value === 'lowest') return a.rating - b.rating || timestamp(b) - timestamp(a);
        if (sort.value === 'photos') return Number(Boolean(b.photos?.length)) - Number(Boolean(a.photos?.length)) || timestamp(b) - timestamp(a);
        return timestamp(b) - timestamp(a);
      });
      const previousCount = append ? cards.children.length : 0;
      if (!append) cards.replaceChildren();
      filtered.slice(previousCount, limit).forEach((review) => cards.append(createCard(review)));
      more.hidden = limit >= filtered.length;
      empty.hidden = filtered.length > 0;
      reset.hidden = !rating && !photoOnly.checked;
      ratingButtons.forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.externalReviewRating) === rating)));
      status.textContent = HWT`Showing ${Math.min(limit, filtered.length)} of ${filtered.length} reviews` + (rating ? ' · ' + HWT`${rating} stars` : '') + (photoOnly.checked ? ' · ' + HWT('With photos only') : '');
      if (append) cards.children[previousCount]?.querySelector('a')?.focus({ preventScroll: true });
    };
    ratingButtons.forEach((button) => button.addEventListener('click', () => {
      const selected = Number(button.dataset.externalReviewRating);
      rating = rating === selected ? 0 : selected;
      limit = initialLimit;
      render();
    }, options));
    [photoOnly, sort].forEach((control) => control.addEventListener('change', () => { limit = initialLimit; render(); }, options));
    reset.addEventListener('click', () => { rating = 0; photoOnly.checked = false; limit = initialLimit; render(); photoOnly.focus(); }, options);
    more.addEventListener('click', () => { limit += pageSize; render(true); }, options);
    instances.set(root, () => { controller.abort(); cards.replaceChildren(); });
    render();
  };
  const mountWithin = (container) => {
    if (container.matches?.('[data-external-reviews]')) mount(container);
    container.querySelectorAll?.('[data-external-reviews]').forEach(mount);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountWithin(document), { once: true });
  else mountWithin(document);
  document.addEventListener('shopify:section:load', (event) => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((cleanup, root) => {
      if (event.target === root || event.target.contains(root)) { cleanup(); instances.delete(root); }
    });
  });
})();

